import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { AppHeader } from "../components/AppHeader";
import { ProgressBar, ColorDot, colorLabel } from "../components/ProgressBar";
import { useAuth } from "../lib/auth";
import { ChevronRight, Calendar, BookMarked, ClipboardCheck, NotebookPen, AlertTriangle, CalendarDays, Download, Lock, Users } from "lucide-react";
import { toast } from "sonner";
import { downloadSecureFile } from "./Timetables";
import { daysUntilLock } from "../lib/planLock";

function PlanReminderBanner({ assignments }) {
  const missing = assignments.filter((a) => a.progress.total === 0);
  const daysLeft = daysUntilLock();
  if (!missing.length || daysLeft <= 0) return null;
  return (
    <div className="mt-8 bg-[var(--just)]/10 border border-[var(--just)] px-5 py-4" data-testid="plan-reminder-banner">
      <div className="flex items-center gap-3">
        <Lock size={18} className="text-[var(--just)] shrink-0" />
        <span className="text-sm">
          <span className="font-bold">J-{daysLeft} — date butoir : lundi 21 septembre inclus.</span>{" "}
          Saisissez votre programme prévisionnel (chapitres + dates) pour {missing.length} matière{missing.length > 1 ? "s" : ""} × classe{missing.length > 1 ? "s" : ""} :
        </span>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        {missing.map((a) => (
          <Link
            key={a.id}
            to={`/teacher/assignment/${a.id}`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-[var(--parchment-2)] hover:border-[var(--maroon)] text-xs transition-colors"
            data-testid={`missing-plan-${a.id}`}
          >
            <span className="font-medium">{a.class.name}</span>
            <span className="text-[var(--ink-soft)]">· {a.subject.name}</span>
            <ChevronRight size={12} className="text-[var(--muted)]" />
          </Link>
        ))}
      </div>
    </div>
  );
}

function GroupCardInfo({ a }) {
  if (!a.level) return null;
  return (
    <div className="mt-3 text-xs text-[var(--ink-soft)] space-y-1" data-testid={`group-info-${a.id}`}>
      <div>
        {a.class_names?.length ? a.class_names.join(" / ") : "Aucun élève affecté"}
        {" · "}{a.student_count ?? 0} élève{(a.student_count ?? 0) > 1 ? "s" : ""}
      </div>
      {a.current_chapter && (
        <div className="truncate">
          En cours : <span className="font-medium text-[var(--ink)]">{a.current_chapter.title}</span>
          {a.current_chapter.end_date && (
            <span className="font-mono-spec text-[var(--muted)]"> · fin prévue {new Date(a.current_chapter.end_date).toLocaleDateString("fr-FR")}</span>
          )}
        </div>
      )}
    </div>
  );
}

function AssignmentCard({ a }) {
  return (
    <Link
      to={`/teacher/assignment/${a.id}`}
      className="group block bg-white border border-[var(--parchment-2)] p-6 hover:border-[var(--maroon)] transition-colors"
      data-testid={`assignment-card-${a.id}`}
    >
      <div className="flex items-start justify-between">
        <div>
          <div className="eyebrow">{a.class.level}</div>
          <div className="font-serif-display text-2xl font-semibold mt-1">{a.class.name}</div>
          <GroupCardInfo a={a} />
        </div>
        <ChevronRight className="text-[var(--muted)] group-hover:text-[var(--maroon)] transition-colors" />
      </div>

      <div className="mt-6 space-y-2">
        <div className="flex items-center justify-between text-sm">
          <span className="text-[var(--ink-soft)]">Avancement</span>
          <span className="font-mono-spec text-[var(--ink)]" data-testid={`progress-text-${a.id}`}>
            {a.progress.ended}/{a.progress.total} ch.
          </span>
        </div>
        <ProgressBar
          percent={a.progress.percent}
          expectedPercent={a.progress.expected_percent}
          color={a.progress.color}
          testId={`progress-bar-${a.id}`}
        />
        <div className="flex items-center justify-between text-xs pt-1">
          <div className="flex items-center gap-2">
            <ColorDot color={a.progress.color} />
            <span className="font-medium">{colorLabel(a.progress.color)}</span>
          </div>
          <span className="font-mono-spec text-[var(--muted)]">{a.progress.percent}% terminé</span>
        </div>
      </div>
    </Link>
  );
}

function AssignmentsList({ loading, assignments, grouped }) {
  if (loading) {
    return <div className="mt-12 text-[var(--ink-soft)]">Chargement...</div>;
  }
  if (assignments.length === 0) {
    return <div className="mt-12 text-[var(--ink-soft)]">Aucune affectation pour le moment.</div>;
  }
  return (
    <div className="mt-12 space-y-12" data-testid="assignments-list">
      {Object.values(grouped).map(({ subject, items }) => (
        <section key={subject.id} data-testid={`subject-section-${subject.code}`}>
          <div className="flex items-baseline justify-between border-b border-[var(--parchment-2)] pb-3 mb-6">
            <h2 className="font-serif-display text-2xl sm:text-3xl font-semibold">{subject.name}</h2>
            <span className="eyebrow">{items.length} classe{items.length > 1 ? "s" : ""}</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.map((a) => (
              <AssignmentCard key={a.id} a={a} />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

export default function TeacherDashboard() {
  const { user } = useAuth();
  const [assignments, setAssignments] = useState([]);
  const [timetable, setTimetable] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/me/assignments").then(({ data }) => {
      setAssignments(data);
      setLoading(false);
    });
    api.get("/me/timetable").then(({ data }) => setTimetable(data.timetable)).catch(() => {});
  }, []);

  const downloadTimetable = async () => {
    try {
      await downloadSecureFile(timetable.path, timetable.name);
    } catch {
      toast.error("Échec du téléchargement de l'emploi du temps");
    }
  };

  // Group by subject (or could group by class) — show subjects with classes inside
  const grouped = assignments.reduce((acc, a) => {
    const key = a.subject.id;
    if (!acc[key]) acc[key] = { subject: a.subject, items: [] };
    acc[key].items.push(a);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-[var(--cream)]">
      <AppHeader />

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="eyebrow">Espace professeur</div>
            <h1 className="font-serif-display text-4xl sm:text-5xl font-semibold mt-2" data-testid="teacher-greeting">
              Bonjour, {user?.name?.split(" ").slice(-1)[0]}.
            </h1>
            <p className="mt-3 text-[var(--ink-soft)] max-w-2xl">
              Voici vos matières et classes. Cliquez sur une carte pour consulter le programme annuel et valider l'avancement.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            {timetable && (
              <button
                onClick={downloadTimetable}
                className="btn-secondary inline-flex items-center gap-2"
                title={timetable.name}
                data-testid="my-timetable-btn"
              >
                <CalendarDays size={16} /> Mon emploi du temps <Download size={13} />
              </button>
            )}
            {user?.role === "assoc_admin" && (
              <Link
                to="/emplois-du-temps"
                className="btn-secondary inline-flex items-center gap-2"
                data-testid="all-timetables-link"
              >
                <CalendarDays size={16} /> Tous les emplois du temps
              </Link>
            )}
            <Link
              to="/teacher/appel"
              className="btn-primary inline-flex items-center gap-2"
              data-testid="appel-link"
            >
              <ClipboardCheck size={16} /> Appel · 5 créneaux
            </Link>
            {user?.manages_level_groups && (
              <Link
                to="/teacher/groupes"
                className="btn-secondary inline-flex items-center gap-2"
                data-testid="level-groups-link"
              >
                <Users size={16} /> Mes groupes de niveaux
              </Link>
            )}
            <Link
              to="/teacher/rattrapages"
              className="btn-secondary inline-flex items-center gap-2"
              data-testid="rattrapages-link"
            >
              <NotebookPen size={16} /> Rattrapages
            </Link>
            <Link
              to="/teacher/signalements"
              className="btn-secondary inline-flex items-center gap-2"
              data-testid="signalements-link"
            >
              <AlertTriangle size={16} /> Signalements
            </Link>
            <Link
              to="/teacher/planning"
              className="btn-secondary inline-flex items-center gap-2"
              data-testid="planning-link"
            >
              <Calendar size={16} /> Planning 2026 — 2027
            </Link>
            {user?.role === "assoc_admin" && (
              <Link
                to="/teacher/etude"
                className="btn-secondary inline-flex items-center gap-2"
                data-testid="etude-link"
              >
                <BookMarked size={16} /> Étude du mercredi
              </Link>
            )}
          </div>
        </div>

        {!loading && <PlanReminderBanner assignments={assignments} />}

        <AssignmentsList loading={loading} assignments={assignments} grouped={grouped} />
      </main>
    </div>
  );
}
